import { RouteType } from "types/RouteType";
import { getAgentAdminStore } from "./AgentAdminStore";

/**
 * AI → Agent Admin.
 *
 * The platform behind the Assistant. AgentChat's own header note says it is
 * "deliberately not an admin console over the platform's 19 services" — this is
 * that console, added when the operator asked for it, and the chat route's note
 * has been updated to point here rather than left contradicting reality.
 *
 * ── WHY THIS IS ADMIN-ONLY WHEN THE CHAT IS NOT ───────────────────────────
 *
 * The chat is staff-visible because the office manager is read-only by construction: her tools
 * are searches over meetings and clients, and a staff member could read all of
 * it through the Meetings and Clients pages anyway.
 *
 * This page is different in kind. It shows what the agent COSTS, what it is
 * told before it answers, what it may call, and what it does unattended — and
 * it can approve a proposed action and stop a schedule. That is provider
 * administration, which HQ already keeps admin-only on AI → Providers and Task
 * Routing, and this is the same class of decision.
 *
 * Tabs load lazily (see AgentAdminStore.loadTab): entering pulls only the tab
 * in view, because the heaviest sections — documents and usage — are the ones
 * most likely to go unread.
 */
const agentAdminRoute: RouteType[] = [
  {
    name: "AgentAdmin",
    path: "/ai/agent-admin",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: "Agent Admin",
        enableRefreshButton: true,
      },
      body: {
        component: () => import("./AgentAdmin.Body"),
        grayBackground: true,
      },
    },
    group: "AI",
    searchAliases: [
      "agent admin",
      "rosie admin",
      "agent usage",
      "agent budgets",
      "agent prompts",
      "agent tools",
      "knowledge base",
      "rag",
      "scheduled runs",
      "agent triggers",
      "proposed actions",
      "trace",
    ],
    hidden: false,
    requiresAuth: true,
    requiresPermission: false,
    requiresAdmin: true,
    // No `channels`: nothing here is a live feed, and subscribing a dozen
    // append-only services would push socket traffic at a page that is read on
    // demand. The header's refresh button is the update path.
    enableAutoFind: false,
    onEnter: ({ rootStore }) => {
      const store = getAgentAdminStore(rootStore);
      void store.loadTab(store.tab);
    },
  },
];

export default agentAdminRoute;
